import { useCallback } from 'react';
import { Platform } from 'react-native';
import type { Dispatch, UnknownAction } from 'redux';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../redux/helper';
import {
  selectActivationCompletedCount,
  selectActivationTrackerPhase,
  selectOwnerActivation,
} from '../redux/ownerActivationSlice';
import {
  dispatchOwnerActivationEvent,
  type ActivationTaskCompletion,
} from '../services/ownerActivationEvents';
import ownerActivationAnalytics from '../services/ownerActivationAnalytics';
import type {
  ActivationTrackerProperties,
  OwnerActivationAnalytics,
  OwnerActivationAnalyticsPlatform,
} from '../services/ownerActivationAnalyticsTypes';
import {
  resolveCompletedSaleEvidence,
  type CheckoutCompletionCandidate,
} from '../services/resolveCompletedSaleEvidence';

/**
 * Completed-sale activation reporting (US5, T077).
 *
 * A terminal real-location sale dispatches SALE_COMPLETED (First-sale
 * evidence). A terminal test-location sale emits practice-only analytics and
 * never completes First sale. Anything non-terminal resolves to nothing.
 *
 * The pure {@link reportCompletedSale} keeps the branch decisions testable
 * without rendering the checkout screens.
 */

export interface ReportCompletedSaleDeps {
  dispatch: Dispatch<UnknownAction>;
  analytics: OwnerActivationAnalytics;
  /** Tracker analytics context for practice-sale events. */
  buildTrackerProperties: () => ActivationTrackerProperties;
}

export const reportCompletedSale = (
  deps: ReportCompletedSaleDeps,
  candidate: CheckoutCompletionCandidate,
  occurredAt: string,
): ActivationTaskCompletion | null => {
  const evidence = resolveCompletedSaleEvidence(candidate);
  if (!evidence) return null;

  if (evidence.locationType === 'real') {
    return dispatchOwnerActivationEvent(deps.dispatch, {
      type: 'SALE_COMPLETED',
      occurredAt,
      source: 'checkout',
      locationId: evidence.locationId,
      locationType: 'real',
    });
  }

  // Terminal test-location sale: practice analytics only, no completion.
  try {
    deps.analytics.track('activation_sale_practice_completed', {
      ...deps.buildTrackerProperties(),
      location_type: 'test',
      sale_state: 'completed',
    });
  } catch {
    // Analytics must never break the checkout success path.
  }
  return null;
};

const analyticsPlatform = (): OwnerActivationAnalyticsPlatform => {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
};

export const useSaleActivation = () => {
  const dispatch = useDispatch();
  const owner = useAppSelector(selectOwnerActivation);
  const trackerPhase = useAppSelector(selectActivationTrackerPhase);
  const completedCount = useAppSelector(selectActivationCompletedCount);
  const businessType = useAppSelector(
    state => state.auth.inApp.account.businessInfo?.type,
  );

  const buildTrackerProperties = useCallback(
    (): ActivationTrackerProperties => ({
      onboarding_version: 1,
      platform: analyticsPlatform(),
      business_type_group: businessType || undefined,
      tracker_phase:
        trackerPhase === 'inactive' ? 'active' : trackerPhase,
      completed_task_count: completedCount,
      wizard_product_created: owner.wizard.productCreatedDuringWizard,
    }),
    [
      businessType,
      completedCount,
      owner.wizard.productCreatedDuringWizard,
      trackerPhase,
    ],
  );

  const report = useCallback(
    (candidate: CheckoutCompletionCandidate) =>
      reportCompletedSale(
        {
          dispatch,
          analytics: ownerActivationAnalytics,
          buildTrackerProperties,
        },
        candidate,
        new Date().toISOString(),
      ),
    [buildTrackerProperties, dispatch],
  );

  return { report };
};

export default useSaleActivation;
